// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useMemo } from 'react';
import {
  DefaultButton,
  IButtonStyles,
  IStackStyles,
  ITextStyles,
  PrimaryButton,
  Stack,
  Text,
  Toggle
} from '@fluentui/react';
import { DrawerSurface } from './DrawerSurface';
import { MOBILE_BUTTON_HEIGHT_REM } from 'components/CommonStyles/mobileButtons';
import {
  CONTINUE_BROWSER_PREFERENCE,
  OPEN_MOBILE_APP_PREFERENCE,
  REMEMBER_PREFERENCE_KEY,
  saveToLocalStorage,
  getFromLocalStorage
} from 'utils/localStorage';

export const DeepLinkDrawer = (props: {
  onContinueInBrowser: () => void;
  onDeepLinkClick: () => void;
  onDismiss: () => void;
}): JSX.Element => (
  <DrawerSurface styles={drawerContainerStyles} onLightDismiss={props.onDismiss}>
    <TryMobileApp {...props} />
  </DrawerSurface>
);

export const TryMobileApp = (props: { onContinueInBrowser: () => void; onDeepLinkClick: () => void }): JSX.Element => {
  const [shouldRememberPreference, setShouldRememberPreference] = React.useState(false);

  // If a user has previously chosen a preference on browser vs app, we can skip the drawer
  const autoContinuePreference = useMemo(() => getFromLocalStorage(REMEMBER_PREFERENCE_KEY), []);
  if (autoContinuePreference) {
    if (autoContinuePreference === CONTINUE_BROWSER_PREFERENCE) {
      props.onContinueInBrowser();
    } else if (autoContinuePreference === OPEN_MOBILE_APP_PREFERENCE) {
      props.onDeepLinkClick();
    }
  }

  const onContinue = (option: 'continueInBrowser' | 'openMobileApp'): void => {
    if (shouldRememberPreference) {
      saveToLocalStorage(REMEMBER_PREFERENCE_KEY, option);
    }

    if (option === CONTINUE_BROWSER_PREFERENCE) {
      props.onContinueInBrowser();
    } else {
      props.onDeepLinkClick();
    }
  };

  return (
    <Stack tokens={{ childrenGap: '1rem' }} styles={drawerContentStyles} horizontalAlign="stretch">
      <Stack.Item>
        <Text styles={drawerTitleStyles}>Try the mobile app</Text>
      </Stack.Item>
      <Stack.Item>
        <DefaultButton
          text="Join using the mobile app"
          onClick={() => onContinue(OPEN_MOBILE_APP_PREFERENCE)}
          styles={drawerButtonStyles}
        />
      </Stack.Item>
      <Stack.Item>
        <PrimaryButton
          text="Continue on browser"
          onClick={() => onContinue(CONTINUE_BROWSER_PREFERENCE)}
          styles={drawerButtonStyles}
        />
      </Stack.Item>
      <Stack.Item>
        <Toggle
          inlineLabel
          label="Remember my preference"
          onText="On"
          offText="Off"
          onChange={(_, checked) => setShouldRememberPreference(checked ?? false)}
        />
      </Stack.Item>
    </Stack>
  );
};

const drawerContainerStyles: IStackStyles = {
  root: {
    // position above context and fill screen
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    margin: '0 !important', // prevent fluent applying margin thinking this is a regular stack item child requiring tokens
    zIndex: 2
  }
};

const drawerContentStyles: IStackStyles = {
  root: {
    padding: '1rem'
  }
};

const drawerTitleStyles: ITextStyles = {
  root: {
    fontWeight: '600',
    fontSize: '1.125rem'
  }
};

const drawerButtonStyles: IButtonStyles = {
  root: {
    width: '100%',
    height: `${MOBILE_BUTTON_HEIGHT_REM}rem`
  }
};
